import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { supabase } from '../../../services/supabase/supabaseClient';
import { useAuth } from '../../../contexts/AuthContext';
import { usePageMeta } from '../../../hooks/usePageMeta';

const CommercialLoginPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  usePageMeta(
    'Đăng nhập | Dịch Tài Liệu',
    'Đăng nhập để dịch tài liệu Word, PowerPoint với AI. Nhận 200.000 credits miễn phí khi đăng ký (đủ 1 báo cáo dày complete, dùng trong 30 ngày).',
    'https://dichtailieu.vn/login',
    { noindex: true },
  );

  const from = (location.state as { from?: string })?.from || '/app';

  // Auto-redirect to app if already logged in (prevents /login showing for authenticated users)
  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, from, navigate]);

  const handleGoogleSignIn = async () => {
    setError(null);
    setIsLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(from)}`,
      },
    });
    setIsLoading(false);
    if (error) {
      setError(`Google OAuth chưa cấu hình: ${error.message}`);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden flex items-center justify-center p-4">
      {/* Layered atmospheric background */}
      <div className="absolute inset-0 bg-gradient-to-b from-emerald-50/60 via-white to-teal-50/70" />
      <div className="absolute inset-0 bg-gradient-to-tr from-emerald-100/30 via-transparent to-teal-100/20" />

      {/* Concentric arcs — Ebolt-style depth */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none opacity-70"
        viewBox="0 0 1200 800"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden
      >
        <defs>
          <linearGradient id="arcGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#0d9488" stopOpacity="0.05" />
          </linearGradient>
        </defs>
        <circle cx="600" cy="420" r="180" fill="none" stroke="url(#arcGrad)" strokeWidth="1" />
        <circle cx="600" cy="420" r="280" fill="none" stroke="url(#arcGrad)" strokeWidth="1" />
        <circle cx="600" cy="420" r="400" fill="none" stroke="url(#arcGrad)" strokeWidth="1" />
        <circle cx="600" cy="420" r="540" fill="none" stroke="url(#arcGrad)" strokeWidth="1" />
        <circle cx="600" cy="420" r="700" fill="none" stroke="url(#arcGrad)" strokeWidth="1" />
      </svg>

      {/* Soft cloud at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-white via-white/60 to-transparent pointer-events-none" />

      {/* Glow blobs */}
      <div className="absolute top-10 left-1/4 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-teal-200/30 rounded-full blur-3xl pointer-events-none" />

      {/* Brand top-left */}
      <Link
        to="/"
        className="absolute top-6 left-6 inline-flex items-center gap-2 z-10 group"
      >
        <img src="/icons/logo-64.png" alt="" className="w-8 h-8" />
        <span className="font-black text-slate-900 group-hover:text-emerald-700 transition-colors">
          Dịch Tài Liệu
        </span>
      </Link>

      {/* Back link top-right */}
      <Link
        to="/"
        className="absolute top-7 right-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700 transition-colors z-10"
      >
        <ArrowLeft className="w-4 h-4" /> Về trang chủ
      </Link>

      {/* Card */}
      <div className="relative w-full max-w-md z-10">
        {/* Subtle glow under card */}
        <div className="absolute -inset-4 bg-gradient-to-br from-emerald-400/10 via-transparent to-teal-400/10 rounded-[2.5rem] blur-2xl" />

        <div className="relative bg-white/40 backdrop-blur-[40px] border border-white/60 rounded-[2rem] shadow-[0_20px_40px_-15px_rgba(0,0,0,0.1)] hover:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)] transition-shadow duration-500 p-8 sm:p-10 overflow-hidden">
          
          {/* Subtle top inner glow (Ebolt style) */}
          <div className="absolute top-0 left-0 right-0 h-48 bg-gradient-to-b from-emerald-200/30 to-transparent pointer-events-none" />
          
          {/* Edge highlight for extra glass feel */}
          <div className="absolute inset-0 rounded-[2rem] border border-white/40 mix-blend-overlay pointer-events-none" />

          <div className="relative z-10">
            {/* Icon tile */}
            <div className="flex justify-center mb-7">
              <div className="relative w-14 h-14 rounded-2xl bg-white shadow-xl shadow-emerald-500/15 border border-emerald-100 flex items-center justify-center">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-50 to-white" />
                <img src="/icons/logo-64.png" alt="" className="relative w-9 h-9" />
              </div>
            </div>

            <h1 className="text-3xl font-black text-slate-900 text-center tracking-tight">
              Chào mừng trở lại
            </h1>
            <p className="text-slate-500 text-center mt-2.5 text-sm leading-relaxed max-w-xs mx-auto">
              Đăng nhập để tiếp tục dịch tài liệu DOCX, PPTX của bạn.
            </p>

            {error && (
              <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              className="mt-8 w-full py-3.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-semibold flex items-center justify-center gap-3 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed shadow-xl shadow-slate-900/20"
            >
              <span className="w-6 h-6 rounded-full bg-white flex items-center justify-center shrink-0">
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
              </span>
              <span>{isLoading ? 'Đang xử lý...' : 'Tiếp tục với Google'}</span>
            </button>

            <p className="mt-7 text-xs text-center text-slate-400 leading-relaxed">
              Bằng việc đăng nhập, bạn đồng ý với{' '}
              <Link to="/tos" className="text-slate-600 hover:text-emerald-700 underline underline-offset-2 decoration-slate-300 hover:decoration-emerald-400">Điều khoản</Link>{' '}và{' '}
              <Link to="/privacy" className="text-slate-600 hover:text-emerald-700 underline underline-offset-2 decoration-slate-300 hover:decoration-emerald-400">Chính sách bảo mật</Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommercialLoginPage;
